import { useMemo } from 'react';

import { useSelector } from 'react-redux';

import {
  hasNewPermissionSel,
  isYouthMemberSel,
  personGuidSel,
} from '@shared/duck/selectors/general.selectors';
import { newPermissions } from '@shared/newPermissions';

import { useSubUnitEnrichment } from './useSubUnitEnrichment';

type UseMySubUnitsOptions = { skip?: boolean };

export function useMySubUnits(
  orgGuid: string,
  { skip = false }: UseMySubUnitsOptions = {},
) {
  const isYouthUser = useSelector(isYouthMemberSel);
  const personGuid = useSelector(personGuidSel);

  const canEditSubProfiles = useSelector(state =>
    hasNewPermissionSel(
      state,
      newPermissions.EDIT_SUB_PROFILE,
      newPermissions.APPROVE_ADVANCEMENTS,
      newPermissions.EDIT_ADVANCEMENTS,
      newPermissions.EDIT_SUB_UNIT,
    ),
  );

  const skipAdults = skip || isYouthUser || !canEditSubProfiles;
  const skipYouths = skip || !isYouthUser || !canEditSubProfiles;

  const { byGuid, isAdultLoading, isYouthLoading } = useSubUnitEnrichment(
    orgGuid,
    { skipAdults, skipYouths },
  );
  const isLoadingMySubUnits = !skip && (isAdultLoading || isYouthLoading);

  const mySubUnits = useMemo(() => {
    // We don't need to know the user subunits if they have full access to the unit
    if (skip || !canEditSubProfiles || !personGuid) return [];

    const [pGuid] = personGuid.split('*');
    const assignments = byGuid[pGuid]?.assignments || [];
    return assignments.filter(sub => sub.denId || sub.patrolId).map(sub => sub);
  }, [canEditSubProfiles, byGuid, personGuid, skip]);

  return { mySubUnits, isLoadingMySubUnits };
}
